import React, { createContext, useState, useEffect, useCallback } from "react";
import { type Parseable, GameParams } from "../validation/game-params";
import { getAvatarId } from "../utils/jwt";
import {
    setUser as setUserInSentry,
    setGameParams,
    captureManualException,
} from "../tracing/sentry";
import { useSnowplowContext } from "../analytics/snowplow";
import { bridge } from "../utils/bridge";

type EmptyGameParams = { tag: "empty" };

export type GameParamsState = {
    gameParams: GameParams | EmptyGameParams;
};

export const DEFAULT_STATE: GameParamsState & {
    requestGameParams(): void;
} = {
    gameParams: { tag: "empty" },
    requestGameParams: () => {},
};

export const GameParamsContext = createContext<
    GameParamsState & { requestGameParams(): void }
>(DEFAULT_STATE);

export const isEmptyGameParams = (
    gameParams: GameParams | EmptyGameParams,
): gameParams is EmptyGameParams =>
    "tag" in gameParams && gameParams.tag === "empty";

export const GameParamsContextProvider = <T extends GameParams>({
    children,
    initialState = DEFAULT_STATE,
    gameParamsParser,
}: {
    children: JSX.Element | JSX.Element[];
    initialState: GameParamsState;
    gameParamsParser: Parseable<T>;
}): JSX.Element => {
    const [state, setState] = useState<GameParamsState>(initialState);
    const { setUser: setUserInSnowplow, setMaintenance } = useSnowplowContext();

    const requestGameParams = useCallback(() => {
        (async () => {
            const { getParams, refreshJwt } = await bridge();
            if (getParams) {
                const { gameParams, validationError } =
                    await getParams(gameParamsParser);
                const isMaintenanceMode = gameParams.maintenance;
                console.log("gameParams", gameParams);

                setState({ gameParams });
                if (window.IS_DEV && validationError && !isMaintenanceMode) {
                    throw validationError;
                }

                if (!window.IS_DEV && validationError && !isMaintenanceMode) {
                    captureManualException(validationError);
                }

                if (isMaintenanceMode !== undefined) {
                    setMaintenance(isMaintenanceMode);
                }

                const { jwt } = gameParams;

                if (!jwt && !isMaintenanceMode) {
                    return await refreshJwt();
                }

                if (jwt) {
                    const avatarId = getAvatarId(jwt);
                    if (avatarId && window.SENTRY_DSN) {
                        setUserInSentry(avatarId);
                    }

                    if (avatarId) {
                        setUserInSnowplow(avatarId);
                    }
                }

                if (gameParams && window.SENTRY_DSN) {
                    setGameParams(gameParams);
                }
            }
        })();
    }, [setState, state]);

    useEffect(() => {
        function handleJWTUpdate(e) {
            const { jwt } = e.detail;
            if (jwt) {
                const avatarId = getAvatarId(jwt);
                if (avatarId && window.SENTRY_DSN) {
                    setUserInSentry(avatarId);
                }

                if (avatarId) {
                    setUserInSnowplow(avatarId);
                }
                setState((oldState: GameParamsState) => ({
                    ...oldState,
                    gameParams: { ...oldState.gameParams, jwt },
                }));
            }
        }
        window.addEventListener("jwtUpdated", handleJWTUpdate);
        return () => {
            window.removeEventListener("jwtUpdated", handleJWTUpdate);
        };
    }, [state]);

    useEffect(() => {
        requestGameParams();
    }, []);

    return (
        <GameParamsContext.Provider value={{ ...state, requestGameParams }}>
            {children}
        </GameParamsContext.Provider>
    );
};

export const useGameParamsContext = <T extends GameParams>() => {
    const ctx = React.useContext(GameParamsContext);

    return ctx as {
        gameParams: T | EmptyGameParams;
        requestGameParams(): void;
    };
};
